import React, { useState } from 'react';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocations } from '../hooks/useLocations.ts';
import LocationDialog from '../components/LocationDialog.tsx';
import GridEmpty from '../components/GridEmpty.tsx';
import apiService from '../services/apiService.ts';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTenant } from '../components/ContextTenant.tsx';

const LocationsPage: React.FC = () => {
  const [params] = useSearchParams();
  const { tenantId: scopedTenantId } = useTenant();
  const tenantId = params.get('tenantId') || (scopedTenantId ? String(scopedTenantId) : '');
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canOpenSpaces = hasRole('SUPER_ADMIN');
  const { rows, loading, reload } = useLocations(tenantId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any|null>(null);

  const handleDelete = async (id: string) => {
    await apiService.deleteLocation(tenantId, id);
    reload();
  };

  const cols: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'address', headerName: 'Address', flex: 2 },
    {
      field: 'actions', headerName: '', width: 120, renderCell: (params)=> (
        <>
          <IconButton onClick={(event)=>{
            event.stopPropagation();
            setEditing(params.row);
            setDialogOpen(true);
          }}><EditIcon/></IconButton>
          <IconButton onClick={(event)=>{
            event.stopPropagation();
            handleDelete(params.row.id);
          }}><DeleteIcon/></IconButton>
        </>
      )
    }
  ];

  return (
    <Box p={2}>
      <IconButton color="primary" onClick={()=>{setEditing(null); setDialogOpen(true);}}><AddIcon/></IconButton>
      {loading ? <CircularProgress/> : (
        <DataGrid
          rows={rows}
          columns={cols}
          autoHeight
          disableRowSelectionOnClick
          components={{ NoRowsOverlay: GridEmpty }}
          onRowClick={(p)=>{
            if (!canOpenSpaces) return;
            const id = p.row.id ?? p.id;
            if(!id) return;
            localStorage.setItem('saSelectedLocation', String(id));
            navigate(`/dashboard/spaces?locationId=${encodeURIComponent(String(id))}`);
          }}
        />
      )}
      <LocationDialog open={dialogOpen} onClose={()=>setDialogOpen(false)} tenantId={tenantId} initial={editing} onSaved={reload} />
    </Box>
  );
};

export default LocationsPage;
